import { ColDef, RowClickedEvent, ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { useSAL2030_R01Store } from "./Store_SAL2030_R01";

import { defaultSAL2030_R01Dto, SAL2030_R01Dto } from "./Dto_SAL2030_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'SAL2030_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<SAL2030_R01Dto> | null>;
	onRowClick?: (row: SAL2030_R01Dto) => void;
}

export const Table_SAL2030_R01 = (props: Props) => {

	const { gridRef, onRowClick } = props;

	const { 
		SAL2030_R01List,
		selectedSAL2030_R01, setSelectedSAL2030_R01, initSelectedSAL2030_R01,
		columnState, setColumnState,
		scrollTop, setScrollTop,
	} = useSAL2030_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const { 
		settingRowIndex, 
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: 'rowKey',
		gridData: SAL2030_R01List,
		defaultGridData: defaultSAL2030_R01Dto,
		selectedRow: selectedSAL2030_R01,
	});

	const getRowId = useCallback((params: any) => {
		const data = params.data;
		if (data?.rowKey) {
			return data.rowKey;
		}
		return params.id;
	}, []);

	const headerNames: Record<string, string> = {
		rowIndex: "",
		compNm: '사업장',
		salesDt: '일자',
		totalAmt: '매출액',
	}

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compNm')(params), []);

	const baseColumnDefs: ColDef<SAL2030_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 100,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '일자', colId: 'salesDt', field: 'salesDt', width: 120,
			cellStyle: (p) => getCellStyle(p, 'salesDt'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params: ValueFormatterParams<SAL2030_R01Dto>) => {
				if (!params.value) return '';
				try {
					const date = new Date(params.value);
					return formatDate(date);
				} catch {
					return String(params.value);
				}
			},
			comparator: cellDateComparator,
		},
		{
			headerName: '매출액', colId: 'totalAmt', field: 'totalAmt', width: 130,
			cellStyle: (p) => getCellStyle(p, 'totalAmt'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => (valueA || 0) - (valueB || 0),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
	], [getCellStyle, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SAL2030_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SAL2030_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);	

	const handleRowClick = useCallback((e: RowClickedEvent<SAL2030_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowKey = e.data?.rowKey;
		if (!rowKey) return;
		const row = SAL2030_R01List.find((item) => item.rowKey === rowKey);
		if (!row) return;
		setSelectedSAL2030_R01(row);
		onRowClick?.(row);
	}, [SAL2030_R01List, onRowClick, setSelectedSAL2030_R01]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		compNm: SAL2030_R01List.length.toLocaleString() + "건",
		totalAmt: SAL2030_R01List.reduce((sum, item) => (sum + (item.totalAmt || 0)), 0).toLocaleString(),
	} as any]), [SAL2030_R01List]);

	return (
		<GridContextWrapper<SAL2030_R01Dto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedSAL2030_R01}
			defaultRowData={defaultSAL2030_R01Dto}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SAL2030_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<SAL2030_R01Dto>({
					idKey: 'rowKey',
					selectedList: selectedSAL2030_R01 ? [selectedSAL2030_R01.rowKey] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}    
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	)
}

